import React, { useState, useEffect } from 'react';
import './SettingsModal.css';
import { YNABCategory } from '../types/ynab';

interface CategoryGroup {
  id: string;
  name: string;
  hidden: boolean;
  deleted: boolean;
  categories: YNABCategory[];
}

interface SettingsModalProps {
  categoryGroups: CategoryGroup[];
  selectedCategoryIds: string[];
  onSave: (selected: string[]) => void;
  onClose: () => void;
}

export const SettingsModal: React.FC<SettingsModalProps> = ({ categoryGroups, selectedCategoryIds, onSave, onClose }) => {
  const [expandedGroups, setExpandedGroups] = useState<string[]>([]);
  const [selected, setSelected] = useState<string[]>(selectedCategoryIds);

  useEffect(() => {
    // Expand groups with selected categories by default
    const initiallyExpanded = categoryGroups
      .filter((group: CategoryGroup) => group.categories.some((cat: YNABCategory) => selected.includes(cat.id)))
      .map((group: CategoryGroup) => group.id);
    setExpandedGroups(initiallyExpanded);
  }, [categoryGroups, selected]);

  const toggleGroup = (groupId: string) => {
    setExpandedGroups(expandedGroups =>
      expandedGroups.includes(groupId)
        ? expandedGroups.filter(id => id !== groupId)
        : [...expandedGroups, groupId]
    );
  };

  const toggleCategory = (categoryId: string) => {
    setSelected(selected =>
      selected.includes(categoryId)
        ? selected.filter(id => id !== categoryId)
        : [...selected, categoryId]
    );
  };

  const handleSave = () => {
    onSave(selected);
    onClose();
  };

  return (
    <div className="settings-modal-overlay">
      <div className="settings-modal">
        <h2>Category Settings</h2>
        <div className="category-groups">
          {categoryGroups.map((group: CategoryGroup) => (
            <div key={group.id} className="category-group">
              <div className="group-header" onClick={() => toggleGroup(group.id)}>
                <span>{expandedGroups.includes(group.id) ? '▼' : '▶'} {group.name}</span>
              </div>
              {expandedGroups.includes(group.id) && (
                <div className="group-categories">
                  {group.categories.map((category: YNABCategory) => (
                    <label key={category.id} className="category-checkbox">
                      <input
                        type="checkbox"
                        checked={selected.includes(category.id)}
                        onChange={() => toggleCategory(category.id)}
                      />
                      {category.name}
                    </label>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
        <div className="settings-actions">
          <button className="save-button" onClick={handleSave}>Save</button>
          <button className="cancel-button" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
};
